import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useSagaCompensations,
  useUpdateCompensationStatus,
  useRetryCompensation,
} from './useNexusSagaReview';

const STATUSES = ['review_required', 'failed', 'pending', 'done', ''];
const STATUS_COLORS = {
  review_required: 'warning',
  failed: 'danger',
  pending: 'info',
  done: 'success',
};

/**
 * Saga / Compensation Review (Spec 5 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-saga-review.md
 * Endpoints: /v1/admin/compensations
 *
 * Typischer Fall: Domain wurde beim Registrar gebucht, Hosting-Step
 * scheiterte → Compensation `domain.unwind` mit status=review_required.
 * Operator entscheidet: Storno einleiten / Domain belassen / Step retryen.
 */
export function NexusSagaReviewPage() {
  const [status, setStatus] = useState('review_required');
  const [action, setAction] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [confirming, setConfirming] = useState(null);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const query = useSagaCompensations({ status, action, tenantId });
  const updateStatus = useUpdateCompensationStatus();
  const retry = useRetryCompensation();
  const compensations = query.data?.compensations ?? [];

  const onMarkDone = (c) =>
    setConfirming({
      id: c.id,
      title: `Compensation ${c.id} als 'done' markieren?`,
      body: 'Du bestätigst dass der Side-Effect manuell bereinigt wurde. Kein Retry mehr.',
      confirmColor: 'success',
      action: () => updateStatus.mutateAsync({ id: c.id, status: 'done' }),
    });

  const onMarkFailed = (c) =>
    setConfirming({
      id: c.id,
      title: `Compensation ${c.id} als 'failed' schließen?`,
      body: 'Der Vorgang wird geschlossen — kein automatischer Retry mehr. Operator kann später manuell reaktivieren.',
      confirmColor: 'danger',
      action: () => updateStatus.mutateAsync({ id: c.id, status: 'failed' }),
    });

  const onRetry = (c) =>
    setConfirming({
      id: c.id,
      title: `Compensation ${c.id} erneut ausführen?`,
      body: `Action: ${c.action} via ${c.plugin_id}. Status wird auf 'pending' gesetzt und der Dispatcher triggert neu.`,
      confirmColor: 'info',
      action: () => retry.mutateAsync({ id: c.id }),
    });

  return (
    <div>
      <PageHeader
        title="Nexus · Saga-Review"
        subtitle={`${compensations.length} Compensations · Auto-Refresh 30s`}
      />

      <Alert color="info" className="small">
        Compensations sind Saga-Rollback-Schritte (z.B. eine Domain
        zurückgeben wenn Hosting fehlschlug). Bei <code>review_required</code>
        hat der Saga-Manager bewusst pausiert — typische Domain-Stornos sind
        Geschäftsentscheidungen, kein Automatik-Job.
      </Alert>

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Status</Label>
              <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 180 }}>
                {STATUSES.map((s) => (
                  <option key={s || 'all'} value={s}>{s || 'Alle'}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Action</Label>
              <Input type="text" bsSize="sm" value={action} onChange={(e) => setAction(e.target.value)} placeholder="z.B. domain.unwind" style={{ minWidth: 200 }} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Tenant-ID</Label>
              <Input type="text" bsSize="sm" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="leer = alle" style={{ minWidth: 120 }} />
            </FormGroup>
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && compensations.length === 0}
        emptyMessage="Keine Compensations für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 100 }}>ID</th>
            <th>Job</th>
            <th>Action</th>
            <th>Plugin</th>
            <th style={{ width: 130 }}>Status</th>
            <th>Error</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 250 }} className="text-end">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {compensations.map((c) => (
            <tr key={c.id}>
              <td><code className="small">{c.id}</code></td>
              <td>
                <Link to={`/admin/settings/nexus/jobs`} className="text-decoration-none">
                  <code className="small">{c.job_id}</code>
                </Link>
                {c.job_type && <div className="small text-muted">{c.job_type}</div>}
              </td>
              <td>
                <Badge color="secondary" pill className="orbit-code-badge-sm">{c.action}</Badge>
                {c.step != null && <div className="small text-muted">Step {c.step}</div>}
              </td>
              <td className="small font-monospace">{c.plugin_id}</td>
              <td>
                <Badge color={STATUS_COLORS[c.status] ?? 'secondary'} pill>{c.status}</Badge>
              </td>
              <td className="small text-danger" style={{ maxWidth: 300 }}>
                <div className="text-truncate" title={c.error}>{c.error}</div>
              </td>
              <td>{c.created_at ? <RelativeTime iso={c.created_at} /> : <span className="text-muted">—</span>}</td>
              <td className="text-end">
                {canWrite && (c.status === 'review_required' || c.status === 'failed') && (
                  <ButtonGroup size="sm">
                    <Button color="success" outline onClick={() => onMarkDone(c)}>
                      ✓ Done
                    </Button>
                    <Button color="danger" outline onClick={() => onMarkFailed(c)}>
                      Failed
                    </Button>
                    <Button color="info" outline onClick={() => onRetry(c)}>
                      🔁 Retry
                    </Button>
                  </ButtonGroup>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <HasPermission perms={['integrations.write']}>
        <ConfirmDialog
          isOpen={!!confirming}
          title={confirming?.title}
          body={confirming?.body}
          confirmLabel="Bestätigen"
          confirmColor={confirming?.confirmColor}
          busy={updateStatus.isPending || retry.isPending}
          onConfirm={async () => {
            await confirming.action();
            setConfirming(null);
          }}
          onCancel={() => setConfirming(null)}
        />
      </HasPermission>
    </div>
  );
}
